import React, { useEffect, useState } from 'react';
import BaseDrawer from './BaseDrawer';
import { useAdminData } from '../../contexts/AdminDataContext';
import { Gift, MapPin, Calendar } from 'lucide-react';
interface RewardDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  rewardId: string | null;
}
const RewardDetailDrawer: React.FC<RewardDetailDrawerProps> = ({
  isOpen,
  onClose,
  rewardId
}) => {
  const {
    rewards,
    updateReward
  } = useAdminData();
  const reward = rewards.find(r => r.id === rewardId);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    type: '',
    cost: 0,
    location: '',
    status: 'Attivo' as 'Attivo' | 'Programmato' | 'Inattivo' | 'Scaduto',
    availabilityCurrent: 0,
    availabilityTotal: 0,
    startDate: '',
    endDate: ''
  });
  useEffect(() => {
    if (reward) {
      setFormData({
        name: reward.name,
        code: reward.code,
        type: reward.type,
        cost: reward.cost,
        location: reward.location,
        status: reward.status,
        availabilityCurrent: reward.availability.current,
        availabilityTotal: reward.availability.total,
        startDate: reward.startDate || '',
        endDate: reward.endDate || ''
      });
    }
  }, [reward]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'cost' || name === 'availabilityCurrent' || name === 'availabilityTotal' ? parseInt(value) || 0 : value
    });
  };
  const handleSave = () => {
    if (rewardId) {
      updateReward(rewardId, {
        name: formData.name,
        code: formData.code,
        type: formData.type,
        cost: formData.cost,
        location: formData.location,
        status: formData.status,
        availability: {
          current: formData.availabilityCurrent,
          total: formData.availabilityTotal,
          unlimited: reward?.availability.unlimited
        },
        startDate: formData.startDate || null,
        endDate: formData.endDate || null
      });
      onClose();
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Attivo':
        return 'bg-green-100 text-green-800';
      case 'Programmato':
        return 'bg-blue-100 text-blue-800';
      case 'Inattivo':
        return 'bg-amber-100 text-amber-800';
      case 'Scaduto':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  if (!reward) return null;
  return <BaseDrawer isOpen={isOpen} onClose={onClose} title="Dettagli Premio" description={`Codice: ${reward.code}`} width="lg" footer={<div className="flex gap-3" data-id="element-749">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-750">
            Annulla
          </button>
          <button onClick={handleSave} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors" data-id="element-751">
            Salva modifiche
          </button>
        </div>} data-id="element-748">
      {/* Image and Name */}
      <div className="flex items-center gap-4" data-id="element-752">
        <img src={reward.image} alt={reward.name} className="h-24 w-24 rounded-lg object-cover" data-id="element-753" />
        <div className="flex-1" data-id="element-754">
          <h3 className="text-xl font-bold text-gray-900" data-id="element-755">{reward.name}</h3>
          <p className="text-sm text-gray-500" data-id="element-756">{reward.partner}</p>
          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-2 ${getStatusColor(reward.status)}`} data-id="element-757">
            {reward.status}
          </span>
        </div>
      </div>

      {/* Editable Fields */}
      <div data-id="element-758">
        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-759">
          Nome Premio
        </label>
        <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-760" />
      </div>

      <div className="grid grid-cols-2 gap-4" data-id="element-761">
        <div data-id="element-762">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-763">
            Codice
          </label>
          <input type="text" name="code" value={formData.code} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent font-mono text-sm" data-id="element-764" />
        </div>
        <div data-id="element-765">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-766">
            Tipo
          </label>
          <input type="text" name="type" value={formData.type} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-767" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4" data-id="element-768">
        <div data-id="element-769">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-770">
            Costo (Crediti)
          </label>
          <input type="number" name="cost" value={formData.cost} onChange={handleChange} min="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-771" />
        </div>
        <div data-id="element-772">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-773">
            Stato
          </label>
          <select name="status" value={formData.status} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-774">
            <option value="Attivo" data-id="element-775">Attivo</option>
            <option value="Programmato" data-id="element-776">Programmato</option>
            <option value="Inattivo" data-id="element-777">Inattivo</option>
            <option value="Scaduto" data-id="element-778">Scaduto</option>
          </select>
        </div>
      </div>

      <div data-id="element-779">
        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-780">
          Luogo
        </label>
        <input type="text" name="location" value={formData.location} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-781" />
      </div>

      {/* Availability */}
      {!reward.availability.unlimited && <div data-id="element-782">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-783">
            Disponibilità
          </label>
          <div className="grid grid-cols-2 gap-4" data-id="element-784">
            <div data-id="element-785">
              <label className="block text-xs text-gray-500 mb-1" data-id="element-786">
                Disponibili
              </label>
              <input type="number" name="availabilityCurrent" value={formData.availabilityCurrent} onChange={handleChange} min="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-787" />
            </div>
            <div data-id="element-788">
              <label className="block text-xs text-gray-500 mb-1" data-id="element-789">Totali</label>
              <input type="number" name="availabilityTotal" value={formData.availabilityTotal} onChange={handleChange} min="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-790" />
            </div>
          </div>
        </div>}

      {/* Date Range */}
      <div data-id="element-791">
        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-792">
          Periodo Validità
        </label>
        <div className="grid grid-cols-2 gap-4" data-id="element-793">
          <div data-id="element-794">
            <label className="block text-xs text-gray-500 mb-1" data-id="element-795">
              Data Inizio
            </label>
            <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-796" />
          </div>
          <div data-id="element-797">
            <label className="block text-xs text-gray-500 mb-1" data-id="element-798">
              Data Fine
            </label>
            <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-799" />
          </div>
        </div>
      </div>

      {/* Read-only Stats */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-800">
        <h4 className="font-semibold text-gray-900 mb-3" data-id="element-801">Statistiche</h4>
        <div className="grid grid-cols-2 gap-4" data-id="element-802">
          <div data-id="element-803">
            <p className="text-xs text-gray-500" data-id="element-804">Riscatti Totali</p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-805">
              {reward.redemptionsTotal}
            </p>
          </div>
          <div data-id="element-806">
            <p className="text-xs text-gray-500" data-id="element-807">Ultimo Riscatto</p>
            <p className="text-sm font-medium text-gray-900" data-id="element-808">
              {reward.lastRedemption}
            </p>
          </div>
        </div>
      </div>
    </BaseDrawer>;
};
export default RewardDetailDrawer;